import React, { useState } from 'react';
import ReactPlayer from 'react-player';
import { FieldArray } from 'formik';
import InputField from './inputFields/InputField';
import TextField from './inputFields/TextField';
import { TiDeleteOutline } from 'react-icons/ti';
import { CgAdd } from 'react-icons/cg';

const ProductDetailsTabView = ({ productDetails, editable = false }) => {
    const [activeTab, setActiveTab] = useState('TECHNICAL_DETAILS');

    const tabs = [
        { name: "Technical Details", key: 'TECHNICAL_DETAILS' },
        { name: "Benefits", key: 'BENEFITS' },
        { name: "Use Cases", key: 'USECASE' },
        { name: "In Box", key: 'IN_BOX' },
        { name: "Additional Details", key: 'ADDITIONAL_DETAILS' },
    ];

    return (
        <div className="space-y-5">
            <div className="overflow-hidden rounded-xl border border-gray-100 bg-gray-50 p-1">
                <ul className="flex items-center text-sm font-medium overflow-x-auto whitespace-nowrap">
                    {tabs.map((tab) => (
                        <li className="flex-1 my-1 px-4 mx-0" key={tab.key}>
                            <p
                                onClick={(e) => {
                                    e.preventDefault();
                                    setActiveTab(tab.key);
                                }}
                                className={`relative flex items-center cursor-pointer justify-center gap-2 border-primary px-3 py-2 ${activeTab === tab.key
                                    ? 'text-primary border-b-2'
                                    : 'text-gray-500 hover:bg-white hover:text-gray-700 hover:border-b-2'
                                    }`}
                            >
                                {tab.name}
                            </p>
                        </li>
                    ))}
                </ul>
            </div>
            <div className="px-4 md:px-4">
                {activeTab === 'TECHNICAL_DETAILS' && (
                    editable ? (
                        <FieldArray name="product_details.TECHNICAL_DETAILS">
                            {({ push, remove }) => (
                                <div className="space-y-2 lg:mx-40">
                                    {(productDetails.TECHNICAL_DETAILS || []).map((_, index) => (
                    					<div key={index} className="flex flex-row justify-center gap-2 items-center">
                                            <InputField name={`product_details.TECHNICAL_DETAILS[${index}].key`} label="Key" />
                                            <InputField name={`product_details.TECHNICAL_DETAILS[${index}].value`} label="Value" />
                                            <div className="md:col-span-2 flex justify-end">
                                                <button type="button" onClick={() => remove(index)} className="p-2 text-red-500 hover:text-red-700">
                                                    <TiDeleteOutline size={20} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                    <button type="button" onClick={() => push({ key: '', value: '' })} className="inline-flex items-center gap-2 text-primary hover:text-blue-700">
                                        <CgAdd size={18} /> Add Row
                                    </button>
                                </div>
                            )}
                        </FieldArray>
                    ) : (
                        <ul className="list-disc list-inside lg:mx-96">
                            {(productDetails.TECHNICAL_DETAILS || []).map((item, index) => (
                                <li key={index} className='flex flex-row w-full'>
                                    <div className="text-xs md:text-md flex w-full px-4 py-2 border border-black font-bold items-center">{item.key}</div>
                                    <div className="text-xs md:text-md flex w-full px-4 py-2 border border-black">{item.value}</div>
                                </li>
                            ))}
                        </ul>
                    )
                )}

                {activeTab === 'BENEFITS' && (
                    editable ? (
                        <FieldArray name="product_details.BENEFITS">
                            {({ push, remove }) => (
                                <div className="space-y-2 lg:mx-40">
                                    {(productDetails.BENEFITS || []).map((_, index) => (
                                        <div key={index} className="flex items-center gap-2">
                                            <TextField name={`product_details.BENEFITS[${index}].value`} label={`Benefit ${index + 1}`} />
                                            <button type="button" onClick={() => remove(index)} className="p-2 text-red-500 hover:text-red-700">
                                                <TiDeleteOutline size={20} />
                                            </button>
                                        </div>
                                    ))}
                                    <button type="button" onClick={() => push({ value: '' })} className="inline-flex items-center gap-2 text-primary hover:text-blue-700">
                                        <CgAdd size={18} /> Add Benefit
                                    </button>
                                </div>
                            )}
                        </FieldArray>
                    ) : (
                        <ul className="list-disc list-inside">
                            {(productDetails.BENEFITS || []).map((item, index) => (
                                <li key={index}>{item.value}</li>
                            ))}
                        </ul>
                    )
                )}

                {activeTab === 'USECASE' && (
                    editable ? (
                        <FieldArray name="product_details.USECASE">
                            {({ push, remove }) => (
                                <div className="space-y-3 lg:mx-40">
                                    {(productDetails.USECASE || []).map((_, index) => (
                                        <div key={index} className="flex flex-row justify-center gap-2">
                                            <TextField name={`product_details.USECASE[${index}].value`} label={`Description ${index + 1}`} />
                                            <InputField name={`product_details.USECASE[${index}].link`} label="Video Link (optional)" />
                                            <div className="flex items-center justify-between">
                                                {productDetails.USECASE?.[index]?.link ? (
                                                    <div className="w-full max-w-xl">
                                                        <ReactPlayer url={productDetails.USECASE[index].link} />
                                                    </div>
                                                ) : <div />}
                                                <button type="button" onClick={() => remove(index)} className="p-2 text-red-500 hover:text-red-700">
                                                    <TiDeleteOutline size={20} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                    <button type="button" onClick={() => push({ value: '', link: '' })} className="inline-flex items-center gap-2 text-primary hover:text-blue-700">
                                        <CgAdd size={18} /> Add Use Case
                                    </button>
                                </div>
                            )}
                        </FieldArray>
                    ) : (
                        <div>
                            <ul className="list-disc list-inside mb-4">
                                {(productDetails.USECASE || []).map((item, index) => (
                                    item.link ? (
                                        <li key={index} className="flex items-center gap-2 pb-4 justify-center w-full">
                                            <ReactPlayer url={item.link} />
                                        </li>
                                    ) : (
                                        <li key={index}>
                                            <span>{item.value || 'No description available.'}</span>
                                        </li>
                                    )
                                ))}
                            </ul>
                        </div>
                    )
                )}

                {activeTab === 'IN_BOX' && (
                    editable ? (
                        <FieldArray name="product_details.IN_BOX">
                            {({ push, remove }) => (
                                <div className="space-y-2 lg:mx-40">
                                    {(productDetails.IN_BOX || []).map((_, index) => (
                                        <div key={index} className="flex items-center gap-2">
                                            <InputField name={`product_details.IN_BOX[${index}].value`} label={`Item ${index + 1}`} />
                                            <button type="button" onClick={() => remove(index)} className="p-2 text-red-500 hover:text-red-700">
                                                <TiDeleteOutline size={20} />
                                            </button>
                                        </div>
                                    ))}
                                    <button type="button" onClick={() => push({ value: '' })} className="inline-flex items-center gap-2 text-primary hover:text-blue-700">
                                        <CgAdd size={18} /> Add Item
                                    </button>
                                </div>
                            )}
                        </FieldArray>
                    ) : (
                        <ul className="list-disc list-inside">
                            {(productDetails.IN_BOX || []).map((item, index) => (
                                <li key={index}>{item.value}</li>
                            ))}
                        </ul>
                    )
                )}

                {activeTab === 'ADDITIONAL_DETAILS' && (
                    editable ? (
                        <FieldArray name="product_details.ADDITIONAL_DETAILS">
                            {({ push, remove }) => (
                                <div className="space-y-2 lg:mx-40">
                                    {(productDetails.ADDITIONAL_DETAILS || []).map((_, index) => (
                                        <div key={index} className="flex items-center gap-2">
                                            <TextField name={`product_details.ADDITIONAL_DETAILS[${index}].value`} label={`Detail ${index + 1}`} />
                                            <button type="button" onClick={() => remove(index)} className="p-2 text-red-500 hover:text-red-700">
                                                <TiDeleteOutline size={20} />
                                            </button>
                                        </div>
                                    ))}
                                    <button type="button" onClick={() => push({ value: '' })} className="inline-flex items-center gap-2 text-primary hover:text-blue-700">
                                        <CgAdd size={18} /> Add Detail
                                    </button>
                                </div>
                            )}
                        </FieldArray>
                    ) : (
                        <ul className="list-disc list-inside">
                            {(productDetails.ADDITIONAL_DETAILS || []).map((item, index) => (
                                <li key={index}>{item.value}</li>
                            ))}
                        </ul>
                    )
                )}
            </div>
        </div>
    );
};

export default ProductDetailsTabView;
